/* global window */

import { useState } from 'react';
import { useQuery } from 'react-apollo';
import { useLocation } from 'react-router';
import { DILM_CLIENT_CONTEXT } from '../../../shared/constants';
import { getQueryFromLocation } from '../../../shared/utils/urls';
import OfficeListMapQuery from './OfficeListMapQuery.graphql';
import { Coordinates, Office, OfficeListMapQueryResult, Viewport } from './types';

interface UseOfficeListMapStateOptions {
  offices: Office[] | null;
  groupId?: string;
  practiceId?: string;
}

export function useOfficeListMapState({
  offices,
  groupId,
  practiceId,
}: UseOfficeListMapStateOptions) {
  const routeLocation = useLocation();
  const query = getQueryFromLocation(routeLocation);
  const [coordinates, setCoordinates] = useState<Coordinates | null>(
    query.latitude && query.longitude
      ? {
          latitude: parseFloat(query.latitude as string),
          longitude: parseFloat(query.longitude as string),
        }
      : null
  );
  const [location, setLocation] = useState<string | null>(
    query.location ? (query.location as string) : null
  );
  const { data, loading } = useQuery<OfficeListMapQueryResult>(OfficeListMapQuery, {
    context: DILM_CLIENT_CONTEXT,
    variables: { groupId, practiceId, location, ...coordinates },
    skip: offices != null && location == null && coordinates == null,
  });
  const [activeOffice, setActiveOffice] = useState<Office | null>(null);
  const [viewport, setViewport] = useState<Viewport | null>(null);
  const filteredOffices = data?.offices?.nodes ?? (offices || []);
  return {
    coordinates,
    setCoordinates,
    location,
    setLocation,
    loading,
    activeOffice,
    setActiveOffice,
    viewport,
    setViewport,
    offices: filteredOffices,
  };
}

export type LocationFormStatus = 'IDLE' | 'LOADING' | 'ERROR' | 'CURRENT_LOCATION' | 'CUSTOM';

interface UseLocationFormOptions {
  coordinates: Coordinates | null;
  setCoordinates: (coordinates: Coordinates | null) => void;
  location: string | null;
  setLocation: (location: string | null) => void;
}

export function useLocationForm({
  coordinates,
  setCoordinates,
  location,
  setLocation,
}: UseLocationFormOptions) {
  const [status, setStatus] = useState<LocationFormStatus>(
    // eslint-disable-next-line no-nested-ternary
    location ? 'CUSTOM' : coordinates ? 'CURRENT_LOCATION' : 'IDLE'
  );
  const [searchValue, setSearchValue] = useState<string>(location || '');

  function onGetCurrentPositionStart() {
    setStatus('LOADING');
    setSearchValue('Finding Your Location…');
  }

  function onGetCurrentPositionSuccess(newCoordinates: Coordinates) {
    setLocation(null);
    setCoordinates(newCoordinates);
    setStatus('CURRENT_LOCATION');
    setSearchValue('Current Location');
  }

  function onGetCurrentPositionError() {
    setStatus('ERROR');
    setSearchValue(location || '');
  }

  function onGetLocation() {
    if (status === 'LOADING') return;

    onGetCurrentPositionStart();
    window.navigator.geolocation.getCurrentPosition(
      (position) => {
        onGetCurrentPositionSuccess({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      (error) => {
        // eslint-disable-next-line no-console
        console.error(error);
        onGetCurrentPositionError();
      },
      { maximumAge: 1000 * 60 * 10 }
    );
  }
  return {
    status,
    searchValue,
    setSearchValue(value: string) {
      if (status !== 'LOADING' && status !== 'CURRENT_LOCATION') {
        setSearchValue(value);
      }
    },
    onGetCurrentPositionStart,
    onGetCurrentPositionSuccess,
    onGetCurrentPositionError,
    onInputFocus() {
      if (status === 'CURRENT_LOCATION') {
        setSearchValue('');
        setStatus('CUSTOM');
      }
    },
    onSubmit() {
      if (coordinates) {
        setCoordinates(null);
      }
      setLocation(searchValue);
      setStatus('CUSTOM');
    },
    onGetLocation,
  };
}
